import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DkDialogService } from '@dkds/ng-components';

import { AgentCredentialsService } from '../../../features/dashboard/services/agent-credentials.service';
import { McpCredentialDto, ProviderApiKeyDto } from '../../../features/dashboard/models/agent-credentials.dto';
import { ErrorHandlerService } from '../../services/error-handler.service';
import { ToastService } from '../../toast/toast.service';

/**
 * The providers this panel can offer to add. Rows are grouped by whatever provider they
 * carry, so one the backend learns about appears here on its own; this list only decides
 * what can be added from an empty panel.
 */
const API_KEY_PROVIDERS = [{ id: 'anthropic', label: 'Anthropic' }];
const MCP_PROVIDERS = [{ id: 'webflow', label: 'Webflow' }];

interface CredentialGroup<T> {
  provider: string;
  label: string;
  rows: T[];
  /** The row for the scope this panel manages, if it has one. */
  ownScope: T | null;
}

/** Which key form is open. One at a time, so the draft beside it needs no key of its own. */
interface OpenForm {
  provider: string;
  projectId: string | null;
  key: string;
}

/**
 * What an organization lends the agent, listed by scope.
 *
 * Both credentials are scoped to (provider, organization, project) with the project
 * optional, and a run resolves the project's before falling back to the organization's.
 * So there is rarely one of each: the panel lists every one, names the project it belongs
 * to, and manages any of them.
 *
 * It is parameterised by scope rather than written twice. The organization's settings
 * mount it without a project and get the whole inventory; a project's settings mount it
 * with one and get that project's row. The scope it is mounted for is also the scope its
 * add affordances write to, which is what keeps the organization-wide credential
 * reachable when every existing one belongs to a project.
 */
@Component({
  selector: 'app-agent-credentials',
  imports: [FormsModule, DatePipe, NgTemplateOutlet],
  templateUrl: './agent-credentials.component.html',
  styleUrl: './agent-credentials.component.scss',
})
export class AgentCredentialsComponent implements OnChanges {
  @Input({ required: true }) organizationId = '';
  /** The project whose own credentials this manages, or null for the organization's. */
  @Input() projectId: string | null = null;
  @Input() projectName = '';

  private readonly dialogs = inject(DkDialogService);

  public loading = false;
  public loadError = '';

  public keyGroups: CredentialGroup<ProviderApiKeyDto>[] = [];
  public tokenGroups: CredentialGroup<McpCredentialDto>[] = [];

  public form: OpenForm | null = null;
  public draft = '';
  public saving = false;
  public formError = '';

  /** Which row a verdict belongs to — printed against the wrong one it would be a lie. */
  public checking: string | null = null;
  public checkedKey: string | null = null;
  public checkMessage = '';
  public checkFailed = false;

  public connecting: string | null = null;
  public mcpError = '';

  constructor(
    private readonly agentCredentialsService: AgentCredentialsService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly toastService: ToastService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organizationId'] || changes['projectId']) {
      this.load();
    }
  }

  public get scopedToProject(): boolean {
    return !!this.projectId;
  }

  /** The service takes `undefined` for the organization's own scope, never null. */
  private get scope(): string | undefined {
    return this.projectId ?? undefined;
  }

  /** Identifies one scope of one provider, for matching an open form or a verdict. */
  public formKey(provider: string, projectId: string | null): string {
    return `${provider}:${projectId ?? 'organization'}`;
  }

  public scopeLabel(row: { project_name: string | null }): string {
    return row.project_name ?? 'Organization';
  }

  // --- loading ---------------------------------------------------------------

  private load(silent = false): void {
    if (!this.organizationId) return;

    this.closeForm();
    this.clearCheck();
    this.mcpError = '';
    this.loading = !silent;
    this.loadError = '';

    this.agentCredentialsService.getOverview(this.organizationId, this.scope).subscribe({
      next: (res) => {
        this.keyGroups = this.group(res.data.api_keys, API_KEY_PROVIDERS);
        this.tokenGroups = this.group(res.data.mcp_credentials, MCP_PROVIDERS);
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.loadError = this.errorHandler.getUserFriendlyErrorMessage(err);
        this.loading = false;
      },
    });
  }

  private group<T extends { provider: string; project_id: string | null }>(
    rows: T[],
    catalogue: { id: string; label: string }[],
  ): CredentialGroup<T>[] {
    const providers = [...new Set([...catalogue.map((c) => c.id), ...rows.map((r) => r.provider)])];
    return providers.map((provider) => {
      const mine = rows.filter((r) => r.provider === provider);
      return {
        provider,
        label: catalogue.find((c) => c.id === provider)?.label ?? provider,
        rows: mine,
        ownScope: mine.find((r) => r.project_id === this.projectId) ?? null,
      };
    });
  }

  // --- the key form ----------------------------------------------------------

  public openForm(provider: string, projectId: string | null): void {
    this.form = { provider, projectId, key: this.formKey(provider, projectId) };
    this.draft = '';
    this.formError = '';
    this.clearCheck();
  }

  public closeForm(): void {
    this.form = null;
    this.draft = '';
    this.formError = '';
  }

  public get canSave(): boolean {
    return !!this.draft.trim() && !this.saving;
  }

  /**
   * The backend checks the key with the provider before storing it, so this fails in two
   * ways that must not read alike: a rejected key (400) is replaced, an unreachable
   * provider (503) is retried. Either way the previous key is still there.
   */
  public saveKey(): void {
    const form = this.form;
    if (!form || !this.canSave) return;

    this.saving = true;
    this.formError = '';

    this.agentCredentialsService
      .saveApiKey(form.provider, this.organizationId, this.draft.trim(), form.projectId ?? undefined)
      .subscribe({
        next: () => {
          this.saving = false;
          this.load(true);
          this.toastService.success({ message: 'API key saved.' });
        },
        error: (err: HttpErrorResponse) => {
          this.formError = this.errorHandler.getUserFriendlyErrorMessage(err);
          this.saving = false;
        },
      });
  }

  // --- acting on one row -----------------------------------------------------

  public testKey(row: ProviderApiKeyDto): void {
    const key = this.formKey(row.provider, row.project_id);
    this.clearCheck();
    this.checking = key;

    this.agentCredentialsService.checkApiKey(row.provider, this.organizationId, row.project_id ?? undefined).subscribe({
      next: () => this.reportCheck(key, 'This key is working.', false),
      error: (err: HttpErrorResponse) =>
        this.reportCheck(key, this.errorHandler.getUserFriendlyErrorMessage(err), true),
    });
  }

  private reportCheck(key: string, message: string, failed: boolean): void {
    this.checkedKey = key;
    this.checkMessage = message;
    this.checkFailed = failed;
    this.checking = null;
  }

  private clearCheck(): void {
    this.checking = null;
    this.checkedKey = null;
    this.checkMessage = '';
    this.checkFailed = false;
  }

  public async removeKey(row: ProviderApiKeyDto): Promise<void> {
    const confirmed = await this.dialogs.confirm({
      title: 'Remove this API key?',
      message: row.project_name
        ? `${row.project_name} will fall back to the organization's key, and its agent conversations will stop if there isn't one.`
        : 'Agent conversations will stop for every project that has no key of its own.',
      confirmLabel: 'Remove',
      tone: 'destructive',
    });
    if (!confirmed) return;

    this.agentCredentialsService
      .removeApiKey(row.provider, this.organizationId, row.project_id ?? undefined)
      .subscribe({
        next: () => {
          this.load(true);
          this.toastService.success({ message: 'API key removed.' });
        },
        error: (err: HttpErrorResponse) => {
          this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
        },
      });
  }

  /**
   * Hands the browser to the provider's consent screen. Where it comes back to is the
   * guard's problem — the backend redirects there carrying the project, when there was
   * one, and the guard reopens this panel on the scope that was authorized.
   */
  public connect(provider: string, projectId: string | null): void {
    this.connecting = this.formKey(provider, projectId);
    this.mcpError = '';

    this.agentCredentialsService.startMcpOAuth(provider, this.organizationId, projectId ?? undefined).subscribe({
      next: (res) => {
        window.location.href = res.data.url;
      },
      error: (err: HttpErrorResponse) => {
        this.mcpError = this.errorHandler.getUserFriendlyErrorMessage(err);
        this.connecting = null;
      },
    });
  }

  public async disconnect(row: McpCredentialDto): Promise<void> {
    const confirmed = await this.dialogs.confirm({
      title: 'Disconnect Webflow?',
      // Said plainly because it is the opposite of what the word suggests: this forgets
      // our copy, and the authorization stays live in their Webflow account until they
      // remove it there.
      message:
        "The agent will lose access immediately. This doesn't revoke the authorization in Webflow — remove it there too if that's what you want.",
      confirmLabel: 'Disconnect',
      tone: 'destructive',
    });
    if (!confirmed) return;

    this.agentCredentialsService
      .disconnectMcp(row.provider, this.organizationId, row.project_id ?? undefined)
      .subscribe({
        next: () => {
          this.load(true);
          this.toastService.success({ message: 'Webflow disconnected.' });
        },
        error: (err: HttpErrorResponse) => {
          this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
        },
      });
  }
}
